// src/pages/AdminDashboard.js
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';

import {
  AlertCircle,
  AlertTriangle,
  BellIcon,
  Calendar,
  CheckCircle,
  FilePenLineIcon,
  FilePlus2Icon,
  FolderPenIcon,
  HomeIcon,
  ImageIcon,
  LogOut,
  MailIcon,
  Menu,
  MessageSquareIcon,
  NewspaperIcon,
  PackageOpenIcon,
  ScrollTextIcon,
  Settings as SettingsIcon,
  Shield,
  ShoppingBagIcon,
  Star,
  TicketPercentIcon,
  TrendingUp,
  UserPlus,
  Users as UsersIcon,
  X,
} from 'lucide-react';
import BookingManagement from '../components/admin/BookingManagement';
import CategoryManagement from '../components/admin/CategoryManagement';
import ClientsManagement from '../components/admin/ClientsManagement';
import ConfigManagement from '../components/admin/ConfigManagement';
import CouponManagement from '../components/admin/CouponManagement';
import GoogleReviewManagement from '../components/admin/GoogleReviewManagement';
import InvoiceManagement from '../components/admin/InvoiceManagement';
import LeadManagement from '../components/admin/LeadManagement';
import MarketingManagement from '../components/admin/MarketingManagement';
import MediaManagement from '../components/admin/MediaManagement';
import MenuManagement from '../components/admin/MenuManagement';
import NotificationCounter from '../components/admin/NotificationCounter';
import NotificationsManagement from '../components/admin/NotificationsManagement';
import OrderManagement from '../components/admin/OrderManagement';
import Overview from '../components/admin/Overview';
import PageManagement from '../components/admin/PageManagement';
import ProductManagement from '../components/admin/ProductManagement';
import RoleManagement from '../components/admin/RoleManagement';
import SalesReport from '../components/admin/SalesReport';
import SecurityManagement from '../components/admin/SecurityManagement';
import TeamMemberManagement from '../components/admin/TeamMemberManagement';
import TemplateManagement from '../components/admin/TemplateManagement';
import TicketManagement from '../components/admin/TicketManagement';
import UberOrdersPanel from '../components/admin/UberOrdersPanel';
import UserManagement from '../components/admin/UserManagemen';
import { useAuth } from '../context/AuthContext';
import { useNotification } from '../context/NotificationContext';

const AdminDashboard = ({ initialActiveMenu }) => {
  const { id } = useParams();
  const location = useLocation();
  const [activeMenu, setActiveMenu] = useState(initialActiveMenu || 'overview');
  const [notification, setNotification] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const { logout, hasPermission, isAdmin, currentUser } = useAuth();
  const { getCount, unreadCount } = useNotification();

  //for new items in tickets, leads,...
  // const { updateTicketCount, updateLeadCount } = useNotification();

  //  states for mobile view
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
  const [mobileLoading, setMobileLoading] = useState(false);

  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: '',
    message: '',
    onConfirm: null,
    onCancel: null,
  });

  // Redirect non-admin users to login
  useEffect(() => {
    if (!isAdmin && !currentUser) {
      window.location.href = '/login';
    }
  }, [isAdmin, currentUser]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // effect to set active menu based on URL
  useEffect(() => {
    if (initialActiveMenu) {
      setActiveMenu(initialActiveMenu);
    } else {
      const path = location.pathname; // This uses the hook's location, not the global
      if (path.includes('/pitech/pages')) {
        setActiveMenu('pageManagement');
      }
    }
  }, [initialActiveMenu, location.pathname]);

  // Function to handle sidebar tab click on mobile
  const handleSidebarItemClick = menu => {
    if (isMobile) {
      setMobileLoading(true);
      setTimeout(() => {
        setActiveMenu(menu);
        setMobileLoading(false);
        setMobileSidebarVisible(false);
        window.scrollTo(0, 0);
      }, 500);
    } else {
      setActiveMenu(menu);
      window.scrollTo(0, 0);
    }
  };

  // Memoized sidebar items
  const sidebarItems = useMemo(
    () => [
      { label: 'Overview', menu: 'overview', icon: HomeIcon, permission: null },
      { label: 'Uber', menu: 'Uber', icon: HomeIcon },
      {
        label: 'Google Reviews',
        menu: 'googleReviews',
        icon: Star,
        permission: { module: 'marketing', action: 'read' }, // or create a new "reviews" module
      },
      {
        label: 'Booking Management',
        menu: 'bookingManagement',
        icon: Calendar,
        permission: { module: 'system', action: 'manage' },
      },
      {
        label: 'Admin Roles ',
        menu: 'roleManagement',
        icon: Shield,
        permission: { module: 'system', action: 'manage' },
      },
      {
        label: 'Categories',
        menu: 'categories',
        icon: FolderPenIcon,
        permission: { module: 'categories', action: 'read' },
      },
      {
        label: 'Products',
        menu: 'products',
        icon: PackageOpenIcon,
        permission: { module: 'products', action: 'read' },
      },
      {
        label: 'Templates',
        menu: 'templates',
        icon: FilePenLineIcon,
        permission: { module: 'templates', action: 'read' },
      },
      {
        label: 'Orders',
        menu: 'orders',
        icon: ShoppingBagIcon,
        permission: { module: 'orders', action: 'read' },
      },
      {
        label: 'Invoices',
        menu: 'invoiceManagement',
        icon: ScrollTextIcon,
        permission: { module: 'orders', action: 'read' },
      },
      {
        label: 'Clients',
        menu: 'clients',
        icon: UsersIcon,
        permission: { module: 'clients', action: 'read' },
      },
      {
        label: 'Coupons',
        menu: 'couponManagement',
        icon: TicketPercentIcon,
        permission: { module: 'products', action: 'read' },
      },
      {
        label: 'Marketing',
        menu: 'marketing',
        icon: MailIcon,
        permission: { module: 'marketing', action: 'read' },
      },
      {
        label: 'Newsletter',
        menu: 'notifications',
        icon: BellIcon,
        permission: { module: 'marketing', action: 'read' },
      },
      {
        label: 'Leads',
        menu: 'leadManagement',
        icon: UserPlus,
        permission: { module: 'marketing', action: 'read' },
      },
      {
        label: 'Support Tickets',
        menu: 'supportTickets',
        icon: MessageSquareIcon,
        permission: { module: 'tickets', action: 'read' },
      },
      {
        label: 'Blog',
        menu: 'blogManagement',
        icon: NewspaperIcon,
        permission: { module: 'blog', action: 'read' },
      },
      {
        label: 'Pages',
        menu: 'pageManagement',
        icon: FilePlus2Icon,
        permission: { module: 'pages', action: 'read' },
      },
      {
        label: 'Teams',
        menu: 'teamMemberManagement',
        icon: UsersIcon,
        permission: { module: 'pages', action: 'read' },
      },
      {
        label: 'Menus',
        menu: 'menuManagement',
        icon: Menu,
        permission: { module: 'menus', action: 'read' },
      },
      {
        label: 'Media Management',
        menu: 'mediaManagement',
        icon: ImageIcon,
        permission: { module: 'system', action: 'manage' },
      },
      {
        label: 'Configuration',
        menu: 'configuration',
        icon: SettingsIcon,
        permission: { module: 'system', action: 'manage' },
      },
      {
        label: 'Home',
        menu: 'home',
        icon: HomeIcon,
        permission: null, // Always visible
      },
      {
        label: 'Log Out',
        menu: 'logout',
        icon: LogOut,
        permission: null, // Always visible
      },
    ],
    []
  );

  // Updated SidebarItem for a more refined look (modified for mobile behavior)
  const SidebarItem = React.memo(({ label, menu, icon, permission, isNested, parent }) => {
    // Only show nested items if parent is active
    if (isNested && parent !== activeMenu) {
      return null;
    }

    // Check if user has permission to see this menu item
    if (permission && !hasPermission(permission.module, permission.action)) {
      return null; // Don't render items user doesn't have permission for
    }

    const renderBadge = () => {
      if (menu === 'supportTickets') {
        const count = getCount('newTickets');
        if (count > 0) {
          return (
            <span className="ml-auto inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
              {count}
            </span>
          );
        }
      }

      if (menu === 'leadManagement') {
        const count = getCount('newLeads');
        if (count > 0) {
          return (
            <span className="ml-auto inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
              {count}
            </span>
          );
        }
      }

      return null;
    };

    return (
      <button
        onClick={() => handleSidebarItemClick(menu)}
        style={{
          boxShadow: 'none',
          border: 'none',
          transform: 'none',
          margin: 0,
          paddingLeft: isNested ? '24px' : undefined, // Indent nested items
        }}
        className={`w-full flex items-center justify-start text-left px-4 py-2 space-x-2 font-normal rounded transition-colors duration-200 ${
          activeMenu === menu ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-200'
        }`}
      >
        {icon &&
          React.cloneElement(icon, {
            className: `h-5 w-5 ${activeMenu === menu ? 'text-blue-600' : 'text-gray-500'}`,
          })}
        <span>{label}</span>
        {renderBadge()}
      </button>
    );
  });

  const renderSidebar = () => {
    // Hide sidebar if we're in live edit mode (hash navigation present)
    if (
      location.hash &&
      (location.hash.includes('#section-') ||
        location.hash.includes('#add-section') ||
        location.hash.includes('#page'))
    ) {
      return null;
    }
    //  else if (location.pathname.endsWith("/pitech/blog")) {
    //   return null;
    // }

    return (
      <div
        className="w-full md:w-64 bg-white shadow-md overflow-y-auto md:sticky md:top-0"
        style={{ height: 'calc(100vh )', boxShadow: 'none', border: 'none' }}
      >
        {isMobile && (
          <div className="flex justify-between items-center p-4 border-b bg-gray-100">
            <h2 className="text-xl font-semibold">Dashboard Menu</h2>
            <button
              onClick={() => setMobileSidebarVisible(false)}
              className="text-xl font-bold p-2 hover:bg-gray-200 rounded-full h-10 w-10 flex items-center justify-center"
            >
              ×
            </button>
          </div>
        )}
        {!isMobile && (
          <div className="p-4 border-b flex items-center">
            <img src="./images/piTech-logo.png" alt="PiTech Logo" className="h-6 w-6 mr-2" />
            <h2 className="text-xl font-semibold">PiTech Dashboard</h2>
          </div>
        )}
        <menu
          className="sidebar-nav  min-h-screen h-full flex flex-wrap gap-0 p-0  md:flex-col md:space-y-2 md:space-x-0"
          style={{ boxShadow: 'none', border: 'none' }}
        >
          {/* First render the Overview item */}
          <SidebarItem
            className="mb-0"
            key="overview"
            label="Overview"
            menu="overview"
            icon={<HomeIcon className="mr-1" />}
            permission={null} // No permission required for overview
          />

          {/* Conditionally render the Security item right after Overview */}
          {activeMenu === 'security' && (
            <div className="pl-6 mt-0 mb-0">
              <SidebarItem
                key="security"
                label="Security"
                menu="security"
                icon={<Shield className="mr-1" />}
                permission={{ module: 'system', action: 'read' }}
              />
            </div>
          )}
          {activeMenu === 'users' && (
            <div className="pl-6 mt-0 mb-0">
              <SidebarItem
                key="users"
                label="Users"
                menu="users"
                icon={<UsersIcon className="mr-1" />}
                permission={{ module: 'system', action: 'read' }}
              />
            </div>
          )}
          {activeMenu === 'sales' && (
            <div className="pl-6 mt-0 mb-0">
              <SidebarItem
                key="sales"
                label="Sales"
                menu="sales"
                icon={<TrendingUp className="mr-1" />}
                permission={{ module: 'orders', action: 'read' }}
              />
            </div>
          )}

          {/* Render all other sidebar items except Overview (since we already rendered it) */}
          {sidebarItems
            .filter(item => item.menu !== 'overview')
            .map(item => (
              <SidebarItem
                key={item.menu}
                label={item.label}
                menu={item.menu}
                icon={<item.icon className="mr-1" />}
                permission={item.permission}
              />
            ))}
        </menu>
      </div>
    );
  };

  const renderContent = () => {
    switch (activeMenu) {
      case 'overview':
        return <Overview setActiveMenu={setActiveMenu} />;
      case 'Uber':
        return <UberOrdersPanel />;
      case 'googleReviews':
        if (!hasPermission('marketing', 'read')) return <UnauthorizedView />;
        return <GoogleReviewManagement />;
      case 'bookingManagement':
        return <BookingManagement />;
      case 'security':
        if (!hasPermission('system', 'read')) return <UnauthorizedView />;
        return <SecurityManagement />;
      case 'users':
        if (!hasPermission('system', 'read')) return <UnauthorizedView />;
        return <UserManagement />;
      case 'sales':
        if (!hasPermission('orders', 'read')) return <UnauthorizedView />;
        return <SalesReport />;
      case 'categories':
      case 'addCategory':
      case 'editCategory':
        if (!hasPermission('categories', 'read')) return <UnauthorizedView />;
        return (
          <CategoryManagement
            setNotification={setNotification}
            setActiveMenu={setActiveMenu}
            setConfirmModal={setConfirmModal}
          />
        );
      case 'products':
      case 'addProduct':
      case 'editProduct':
        if (!hasPermission('products', 'read')) return <UnauthorizedView />;
        return (
          <React.Suspense
            fallback={
              <div className="flex flex-col items-center justify-center h-64">
                <img src="../images/loading.gif" alt="Loading" />
              </div>
            }
          >
            <ProductManagement
              setNotification={setNotification}
              setActiveMenu={setActiveMenu}
              setConfirmModal={setConfirmModal}
            />
          </React.Suspense>
        );
      case 'templates':
      case 'createTemplate':
      case 'editTemplate':
        if (!hasPermission('templates', 'read')) return <UnauthorizedView />;
        return (
          <TemplateManagement
            setNotification={setNotification}
            setActiveMenu={setActiveMenu}
            setConfirmModal={setConfirmModal}
          />
        );
      case 'orders':
        return <OrderManagement />;
      case 'invoiceManagement':
        return <InvoiceManagement />;
      case 'couponManagement':
        return <CouponManagement />;
      case 'clients':
        return <ClientsManagement />;
      case 'marketing':
        return <MarketingManagement />;
      case 'notifications':
        return <NotificationsManagement />;
      case 'leadManagement':
        // if (!hasPermission("system" || "marketing")) return <UnauthorizedView />;
        return <LeadManagement />;
      case 'supportTickets':
        return <TicketManagement />;
      case 'roleManagement':
        if (!hasPermission('system', 'manage')) return <UnauthorizedView />;
        return <RoleManagement />;
      case 'home':
        window.location.href = '/';
        return null;
      case 'blogManagement':
        // return <BlogManagement />;
        window.location.href = '/pitech/blog';
        return null;
      case 'menuManagement':
        return <MenuManagement />;
      case 'pageManagement':
        if (!hasPermission('pages', 'read')) return <UnauthorizedView />;
        return <PageManagement setActiveMenu={setActiveMenu} />;
      case 'teamMemberManagement':
        return <TeamMemberManagement />;
      case 'mediaManagement':
        if (!hasPermission('system', 'manage')) return <UnauthorizedView />;
        return <MediaManagement />;
      case 'configuration':
        if (!hasPermission('system', 'manage')) return <UnauthorizedView />;
        return <ConfigManagement />;
      case 'logout':
        logout();
        window.location.href = '/';
        return null;
      default:
        return null;
    }
  };

  // Component to show when user doesn't have permission
  const UnauthorizedView = () => (
    <div className="flex flex-col items-center justify-center h-64 text-center">
      <Shield className="w-16 h-16 text-red-500 mb-4" />
      <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
      <p className="text-gray-600 max-w-md">
        You don't have permission to access this section. Please contact your administrator if you
        believe this is an error.
      </p>
    </div>
  );

  // Custom Notification Component
  const NotificationAlert = ({ type, message, onClose }) => {
    const alertStyles = {
      success: 'bg-green-50 border-green-200 text-green-800',
      error: 'bg-red-50 border-red-200 text-red-800',
      warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    };

    const iconStyles = {
      success: 'text-green-400',
      error: 'text-red-400',
      warning: 'text-yellow-400',
    };

    const AlertIcon =
      type === 'success' ? CheckCircle : type === 'error' ? AlertCircle : AlertTriangle;

    return (
      <div className="fixed top-4 right-4 z-50 max-w-md w-full mx-auto">
        <div
          className={`border rounded-lg p-4 shadow-lg ${alertStyles[type]} animate-in slide-in-from-top-2 duration-300`}
        >
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <AlertIcon className={`h-5 w-5 ${iconStyles[type]}`} />
            </div>
            <div className="ml-3 flex-1">
              <p className="text-sm font-medium">{message}</p>
            </div>
            <div className="ml-4 flex-shrink-0">
              <button
                onClick={onClose}
                className={`inline-flex rounded-md p-1.5 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-colors ${
                  type === 'success'
                    ? 'text-green-500 hover:bg-green-100 focus:ring-green-600'
                    : type === 'error'
                    ? 'text-red-500 hover:bg-red-100 focus:ring-red-600'
                    : 'text-yellow-500 hover:bg-yellow-100 focus:ring-yellow-600'
                }`}
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <NotificationCounter />

      {/* Modern Notification */}
      {notification && (
        <NotificationAlert
          type={notification.type}
          message={notification.message}
          onClose={() => setNotification(null)}
        />
      )}

      {/* Delete Confirmation */}
      {confirmModal.show && (
        <div className="fixed z-50 inset-0 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              onClick={() => setConfirmModal({ ...confirmModal, show: false })}
            ></div>

            <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      {confirmModal.title}
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">{confirmModal.message}</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                  onClick={() => {
                    if (confirmModal.onConfirm) confirmModal.onConfirm();
                    setConfirmModal({ ...confirmModal, show: false });
                  }}
                >
                  Delete
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                  onClick={() => {
                    if (confirmModal.onCancel) confirmModal.onCancel();
                    setConfirmModal({ ...confirmModal, show: false });
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isMobile && (
        <div
          className="fixed top-0 left-0 z-50 flex items-center md:hidden"
          style={{ marginTop: '21px', marginLeft: '16px' }}
        >
          <button
            onClick={() => setMobileSidebarVisible(true)}
            className="p-1 rounded-md focus:outline-none hover:bg-gray-700 transition-all duration-200"
            style={{
              boxShadow: 'none',
              border: 'none',
              background: 'transparent',
              transform: 'none',
              padding: 0,
              overflow: 'visible',
            }}
            aria-label="Open menu"
          >
            <Menu className="w-6 h-6 text-gray-300" />
          </button>
        </div>
      )}
      <div className="flex flex-col md:flex-row min-h-screen bg-gray-100 relative">
        {!isMobile && renderSidebar()}
        {isMobile && mobileSidebarVisible && (
          <>
            {/* Dark overlay behind the sidebar */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-40"
              onClick={() => setMobileSidebarVisible(false)}
            ></div>
            {/* Mobile sidebar */}
            <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white">{renderSidebar()}</div>
          </>
        )}
        <div className="flex-1 p-8">
          {mobileLoading && (
            <div className="fixed inset-0 flex flex-col items-center justify-center bg-white z-50">
              <img src="../images/loading.gif" />
            </div>
          )}
          {renderContent()}
        </div>

        <style>
          {`
          .sidebar-nav {
            overflow-y: visible !important;

          }
          @media screen and (max-height: 768px) {
            .sidebar-nav {
              flex-wrap: nowrap !important;
              flex-direction: column !important;
              overflow-y: visible !important;
              overflow-x: hidden !important;
            }
          }
        `}
        </style>
      </div>
    </>
  );
};

export default AdminDashboard;

// // before media management in components
// // src/pages/AdminDashboard.js
// import React, {
//   useState,
//   useEffect,
//   useCallback,
//   useMemo,
//   Suspense,
// } from "react";
// import { useParams, useLocation } from "react-router-dom";

// import {
//   HomeIcon,
//   FolderPlusIcon,
//   FolderPenIcon,
//   PackageOpenIcon,
//   PackagePlusIcon,
//   FilePlus2Icon,
//   FilePenLineIcon,
//   TicketPercentIcon,
//   ShoppingBagIcon,
//   ImageIcon,
//   BellIcon,
//   Users as UsersIcon,
//   NewspaperIcon as Notification,
//   Loader2 as Loader2Icon,
//   MailIcon,
//   LogOut,
//   NewspaperIcon,
//   Group,
//   Shield,
//   TrendingUp,
//   DollarSign,
//   MessageSquareIcon,
//   ScrollTextIcon,
//   UserPlus,
//   Star,
//   Calendar,
//   Clock,
//   Users,
//   Settings as SettingsIcon,
// } from "lucide-react";
// import CategoryManagement from "../components/admin/CategoryManagement";
// import ProductManagement from "../components/admin/ProductManagement";
// import OrderManagement from "../components/admin/OrderManagement";
// import CouponManagement from "../components/admin/CouponManagement";
// import Overview from "../components/admin/Overview";
// import TemplateManagement from "../components/admin/TemplateManagement";
// import MediaManager from "./admin/MediaManager";
// import ClientsManagement from "../components/admin/ClientsManagement";
// import NotificationsManagement from "../components/admin/NotificationsManagement";
// import MarketingManagement from "../components/admin/MarketingManagement";
// import BlogManagement from "../components/admin/BlogManagement";
// import RoleManagement from "../components/admin/RoleManagement";
// import { Menu } from "lucide-react";
// import { useAuth } from "../context/AuthContext";
// import SecurityManagement from "../components/admin/SecurityManagement";
// import UserManagement from "../components/admin/UserManagemen";
// import SalesReport from "../components/admin/SalesReport";
// import TicketManagement from "../components/admin/TicketManagement";
// import PageManagement from "../components/admin/PageManagement";
// import MenuManagement from "../components/admin/MenuManagement";
// import TeamMemberManagement from "../components/admin/TeamMemberManagement";
// import InvoiceManagement from "../components/admin/InvoiceManagement";
// import LeadManagement from "../components/admin/LeadManagement";
// import UberOrdersPanel from "../components/admin/UberOrdersPanel";
// import ConfigManagement from "../components/admin/ConfigManagement";
// import GoogleReviewManagement from "../components/admin/GoogleReviewManagement";
// import { useNotification } from "../context/NotificationContext";
// import NotificationCounter from "../components/admin/NotificationCounter";
// import BookingManagement from "../components/admin/BookingManagement";

// const AdminDashboard = ({ initialActiveMenu }) => {
//   const { id } = useParams();
//   const location = useLocation();
//   const [activeMenu, setActiveMenu] = useState(initialActiveMenu || "overview");
//   const [notification, setNotification] = useState(null);
//   const [confirmDialog, setConfirmDialog] = useState(null);
//   const { logout, hasPermission, isAdmin, currentUser } = useAuth();
//   const { getCount, unreadCount } = useNotification();

//   //for new items in tickets, leads,...
//   // const { updateTicketCount, updateLeadCount } = useNotification();

//   //  states for mobile view
//   const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
//   const [mobileSidebarVisible, setMobileSidebarVisible] = useState(false);
//   const [mobileLoading, setMobileLoading] = useState(false);

//   const [confirmModal, setConfirmModal] = useState({
//     show: false,
//     title: "",
//     message: "",
//     onConfirm: null,
//     onCancel: null,
//   });

//   // Redirect non-admin users to login
//   useEffect(() => {
//     if (!isAdmin && !currentUser) {
//       window.location.href = "/login";
//     }
//   }, [isAdmin, currentUser]);

//   useEffect(() => {
//     const handleResize = () => {
//       setIsMobile(window.innerWidth < 768);
//     };
//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   // effect to set active menu based on URL
//   useEffect(() => {
//     if (initialActiveMenu) {
//       setActiveMenu(initialActiveMenu);
//     } else {
//       const path = location.pathname; // This uses the hook's location, not the global
//       if (path.includes("/pitech/pages")) {
//         setActiveMenu("pageManagement");
//       }
//     }
//   }, [initialActiveMenu, location.pathname]);

//   // Function to handle sidebar tab click on mobile
//   const handleSidebarItemClick = (menu) => {
//     if (isMobile) {
//       setMobileLoading(true);
//       setTimeout(() => {
//         setActiveMenu(menu);
//         setMobileLoading(false);
//         setMobileSidebarVisible(false);
//         window.scrollTo(0, 0);
//       }, 500);
//     } else {
//       setActiveMenu(menu);
//       window.scrollTo(0, 0);
//     }
//   };

//   // Memoized sidebar items
//   const sidebarItems = useMemo(
//     () => [
//       { label: "Overview", menu: "overview", icon: HomeIcon, permission: null },
//       { label: "Uber", menu: "Uber", icon: HomeIcon },
//       {
//         label: "Google Reviews",
//         menu: "googleReviews",
//         icon: Star,
//         permission: { module: "marketing", action: "read" }, // or create a new "reviews" module
//       },
//       {
//         label: "Booking Management",
//         menu: "bookingManagement",
//         icon: Calendar,
//         permission: { module: "system", action: "manage" },
//       },
//       {
//         label: "Admin Roles ",
//         menu: "roleManagement",
//         icon: Shield,
//         permission: { module: "system", action: "manage" },
//       },
//       {
//         label: "Categories",
//         menu: "categories",
//         icon: FolderPenIcon,
//         permission: { module: "categories", action: "read" },
//       },
//       {
//         label: "Products",
//         menu: "products",
//         icon: PackageOpenIcon,
//         permission: { module: "products", action: "read" },
//       },
//       {
//         label: "Templates",
//         menu: "templates",
//         icon: FilePenLineIcon,
//         permission: { module: "templates", action: "read" },
//       },
//       {
//         label: "Orders",
//         menu: "orders",
//         icon: ShoppingBagIcon,
//         permission: { module: "orders", action: "read" },
//       },
//       {
//         label: "Invoices",
//         menu: "invoiceManagement",
//         icon: ScrollTextIcon,
//         permission: { module: "orders", action: "read" },
//       },
//       {
//         label: "Clients",
//         menu: "clients",
//         icon: UsersIcon,
//         permission: { module: "clients", action: "read" },
//       },
//       {
//         label: "Coupons",
//         menu: "couponManagement",
//         icon: TicketPercentIcon,
//         permission: { module: "products", action: "read" },
//       },
//       {
//         label: "Marketing",
//         menu: "marketing",
//         icon: MailIcon,
//         permission: { module: "marketing", action: "read" },
//       },
//       {
//         label: "Newsletter",
//         menu: "notifications",
//         icon: BellIcon,
//         permission: { module: "marketing", action: "read" },
//       },
//       {
//         label: "Leads",
//         menu: "leadManagement",
//         icon: UserPlus,
//         permission: { module: "marketing", action: "read" },
//       },
//       {
//         label: "Support Tickets",
//         menu: "supportTickets",
//         icon: MessageSquareIcon,
//         permission: { module: "tickets", action: "read" },
//       },
//       {
//         label: "Blog",
//         menu: "blogManagement",
//         icon: NewspaperIcon,
//         permission: { module: "blog", action: "read" },
//       },
//       {
//         label: "Pages",
//         menu: "pageManagement",
//         icon: FilePlus2Icon,
//         permission: { module: "pages", action: "read" },
//       },
//       {
//         label: "Teams",
//         menu: "teamMemberManagement",
//         icon: UsersIcon,
//         permission: { module: "pages", action: "read" },
//       },
//       {
//         label: "Menus",
//         menu: "menuManagement",
//         icon: Menu,
//         permission: { module: "menus", action: "read" },
//       },
//       {
//         label: "Media Manager",
//         menu: "mediaManager",
//         icon: ImageIcon,
//         permission: { module: "products", action: "read" },
//       },
//       {
//         label: "Configuration",
//         menu: "configuration",
//         icon: SettingsIcon,
//         permission: { module: "system", action: "manage" },
//       },
//       {
//         label: "Home",
//         menu: "home",
//         icon: HomeIcon,
//         permission: null, // Always visible
//       },
//       {
//         label: "Log Out",
//         menu: "logout",
//         icon: LogOut,
//         permission: null, // Always visible
//       },
//     ],
//     []
//   );

//   // Updated SidebarItem for a more refined look (modified for mobile behavior)
//   const SidebarItem = React.memo(
//     ({ label, menu, icon, permission, isNested, parent }) => {
//       // Only show nested items if parent is active
//       if (isNested && parent !== activeMenu) {
//         return null;
//       }

//       // Check if user has permission to see this menu item
//       if (permission && !hasPermission(permission.module, permission.action)) {
//         return null; // Don't render items user doesn't have permission for
//       }

//       const renderBadge = () => {
//         if (menu === "supportTickets") {
//           const count = getCount("newTickets");
//           if (count > 0) {
//             return (
//               <span className="ml-auto inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
//                 {count}
//               </span>
//             );
//           }
//         }

//         if (menu === "leadManagement") {
//           const count = getCount("newLeads");
//           if (count > 0) {
//             return (
//               <span className="ml-auto inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white bg-red-500 rounded-full">
//                 {count}
//               </span>
//             );
//           }
//         }

//         return null;
//       };

//       return (
//         <button
//           onClick={() => handleSidebarItemClick(menu)}
//           style={{
//             boxShadow: "none",
//             border: "none",
//             transform: "none",
//             margin: 0,
//             paddingLeft: isNested ? "24px" : undefined, // Indent nested items
//           }}
//           className={`w-full flex items-center justify-start text-left px-4 py-2 space-x-2 font-normal rounded transition-colors duration-200 ${
//             activeMenu === menu
//               ? "bg-blue-100 text-blue-600"
//               : "text-gray-600 hover:bg-gray-200"
//           }`}
//         >
//           {icon &&
//             React.cloneElement(icon, {
//               className: `h-5 w-5 ${
//                 activeMenu === menu ? "text-blue-600" : "text-gray-500"
//               }`,
//             })}
//           <span>{label}</span>
//           {renderBadge()}
//         </button>
//       );
//     }
//   );

//   const renderSidebar = () => {
//     // Hide sidebar if we're in live edit mode (hash navigation present)
//     if (
//       location.hash &&
//       (location.hash.includes("#section-") ||
//         location.hash.includes("#add-section") ||
//         location.hash.includes("#page"))
//     ) {
//       return null;
//     }
//     //  else if (location.pathname.endsWith("/pitech/blog")) {
//     //   return null;
//     // }

//     return (
//       <div
//         className="w-full md:w-64 bg-white shadow-md overflow-y-auto md:sticky md:top-0"
//         style={{ height: "calc(100vh )", boxShadow: "none", border: "none" }}
//       >
//         {isMobile && (
//           <div className="flex justify-between items-center p-4 border-b bg-gray-100">
//             <h2 className="text-xl font-semibold">Dashboard Menu</h2>
//             <button
//               onClick={() => setMobileSidebarVisible(false)}
//               className="text-xl font-bold p-2 hover:bg-gray-200 rounded-full h-10 w-10 flex items-center justify-center"
//             >
//               ×
//             </button>
//           </div>
//         )}
//         {!isMobile && (
//           <div className="p-4 border-b flex items-center">
//             <img
//               src="./images/piTech-logo.png"
//               alt="PiTech Logo"
//               className="h-6 w-6 mr-2"
//             />
//             <h2 className="text-xl font-semibold">PiTech Dashboard</h2>
//           </div>
//         )}
//         <menu
//           className="sidebar-nav  min-h-screen h-full flex flex-wrap gap-0 p-0  md:flex-col md:space-y-2 md:space-x-0"
//           style={{ boxShadow: "none", border: "none" }}
//         >
//           {/* First render the Overview item */}
//           <SidebarItem
//             className="mb-0"
//             key="overview"
//             label="Overview"
//             menu="overview"
//             icon={<HomeIcon className="mr-1" />}
//             permission={null} // No permission required for overview
//           />

//           {/* Conditionally render the Security item right after Overview */}
//           {activeMenu === "security" && (
//             <div className="pl-6 mt-0 mb-0">
//               <SidebarItem
//                 key="security"
//                 label="Security"
//                 menu="security"
//                 icon={<Shield className="mr-1" />}
//                 permission={{ module: "system", action: "read" }}
//               />
//             </div>
//           )}
//           {activeMenu === "users" && (
//             <div className="pl-6 mt-0 mb-0">
//               <SidebarItem
//                 key="users"
//                 label="Users"
//                 menu="users"
//                 icon={<UsersIcon className="mr-1" />}
//                 permission={{ module: "system", action: "read" }}
//               />
//             </div>
//           )}
//           {activeMenu === "sales" && (
//             <div className="pl-6 mt-0 mb-0">
//               <SidebarItem
//                 key="sales"
//                 label="Sales"
//                 menu="sales"
//                 icon={<TrendingUp className="mr-1" />}
//                 permission={{ module: "orders", action: "read" }}
//               />
//             </div>
//           )}

//           {/* Render all other sidebar items except Overview (since we already rendered it) */}
//           {sidebarItems
//             .filter((item) => item.menu !== "overview")
//             .map((item) => (
//               <SidebarItem
//                 key={item.menu}
//                 label={item.label}
//                 menu={item.menu}
//                 icon={<item.icon className="mr-1" />}
//                 permission={item.permission}
//               />
//             ))}
//         </menu>
//       </div>
//     );
//   };

//   const renderContent = () => {
//     switch (activeMenu) {
//       case "overview":
//         return <Overview setActiveMenu={setActiveMenu} />;
//       case "Uber":
//         return <UberOrdersPanel />;
//       case "googleReviews":
//         if (!hasPermission("marketing", "read")) return <UnauthorizedView />;
//         return <GoogleReviewManagement />;
//       case "bookingManagement":
//         return <BookingManagement />;
//       case "security":
//         if (!hasPermission("system", "read")) return <UnauthorizedView />;
//         return <SecurityManagement />;
//       case "users":
//         if (!hasPermission("system", "read")) return <UnauthorizedView />;
//         return <UserManagement />;
//       case "sales":
//         if (!hasPermission("orders", "read")) return <UnauthorizedView />;
//         return <SalesReport />;
//       case "categories":
//       case "addCategory":
//       case "editCategory":
//         if (!hasPermission("categories", "read")) return <UnauthorizedView />;
//         return (
//           <CategoryManagement
//             setNotification={setNotification}
//             setActiveMenu={setActiveMenu}
//             setConfirmModal={setConfirmModal}
//           />
//         );
//       case "products":
//       case "addProduct":
//       case "editProduct":
//         if (!hasPermission("products", "read")) return <UnauthorizedView />;
//         return (
//           <React.Suspense
//             fallback={
//               <div className="flex flex-col items-center justify-center h-64">
//                 <img src="../images/loading.gif" alt="Loading" />
//               </div>
//             }
//           >
//             <ProductManagement
//               setNotification={setNotification}
//               setActiveMenu={setActiveMenu}
//               setConfirmModal={setConfirmModal}
//             />
//           </React.Suspense>
//         );
//       case "templates":
//       case "createTemplate":
//       case "editTemplate":
//         if (!hasPermission("templates", "read")) return <UnauthorizedView />;
//         return (
//           <TemplateManagement
//             setNotification={setNotification}
//             setActiveMenu={setActiveMenu}
//             setConfirmModal={setConfirmModal}
//           />
//         );
//       case "orders":
//         return <OrderManagement />;
//       case "invoiceManagement":
//         return <InvoiceManagement />;
//       case "couponManagement":
//         return <CouponManagement />;
//       case "clients":
//         return <ClientsManagement />;
//       case "marketing":
//         return <MarketingManagement />;
//       case "notifications":
//         return <NotificationsManagement />;
//       case "leadManagement":
//         // if (!hasPermission("system" || "marketing")) return <UnauthorizedView />;
//         return <LeadManagement />;
//       case "supportTickets":
//         return <TicketManagement />;
//       case "roleManagement":
//         if (!hasPermission("system", "manage")) return <UnauthorizedView />;
//         return <RoleManagement />;
//       case "home":
//         window.location.href = "/";
//         return null;
//       case "blogManagement":
//         // return <BlogManagement />;
//         window.location.href = "/pitech/blog";
//         return null;
//       case "menuManagement":
//         return <MenuManagement />;
//       case "pageManagement":
//         if (!hasPermission("pages", "read")) return <UnauthorizedView />;
//         return <PageManagement setActiveMenu={setActiveMenu} />;
//       case "teamMemberManagement":
//         return <TeamMemberManagement />;
//       case "mediaManager":
//         return <MediaManager />;
//       case "configuration":
//         if (!hasPermission("system", "manage")) return <UnauthorizedView />;
//         return <ConfigManagement />;
//       case "logout":
//         logout();
//         window.location.href = "/";
//         return null;
//       default:
//         return null;
//     }
//   };

//   // Component to show when user doesn't have permission
//   const UnauthorizedView = () => (
//     <div className="flex flex-col items-center justify-center h-64 text-center">
//       <Shield className="w-16 h-16 text-red-500 mb-4" />
//       <h2 className="text-2xl font-bold text-gray-800 mb-2">Access Denied</h2>
//       <p className="text-gray-600 max-w-md">
//         You don't have permission to access this section. Please contact your
//         administrator if you believe this is an error.
//       </p>
//     </div>
//   );

//   return (
//     <>
//       <NotificationCounter />

//       {/* Delete Confirmation */}
//       {confirmModal.show && (
//         <div className="fixed z-50 inset-0 overflow-y-auto">
//           <div className="flex items-center justify-center min-h-screen px-4">
//             <div
//               className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
//               onClick={() => setConfirmModal({ ...confirmModal, show: false })}
//             ></div>

//             <div className="bg-white rounded-lg overflow-hidden shadow-xl transform transition-all sm:max-w-lg sm:w-full">
//               <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
//                 <div className="sm:flex sm:items-start">
//                   <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
//                     <svg
//                       className="h-6 w-6 text-red-600"
//                       xmlns="http://www.w3.org/2000/svg"
//                       fill="none"
//                       viewBox="0 0 24 24"
//                       stroke="currentColor"
//                     >
//                       <path
//                         strokeLinecap="round"
//                         strokeLinejoin="round"
//                         strokeWidth="2"
//                         d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
//                       />
//                     </svg>
//                   </div>
//                   <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
//                     <h3 className="text-lg leading-6 font-medium text-gray-900">
//                       {confirmModal.title}
//                     </h3>
//                     <div className="mt-2">
//                       <p className="text-sm text-gray-500">
//                         {confirmModal.message}
//                       </p>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//               <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
//                 <button
//                   type="button"
//                   className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm"
//                   onClick={() => {
//                     if (confirmModal.onConfirm) confirmModal.onConfirm();
//                     setConfirmModal({ ...confirmModal, show: false });
//                   }}
//                 >
//                   Delete
//                 </button>
//                 <button
//                   type="button"
//                   className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
//                   onClick={() => {
//                     if (confirmModal.onCancel) confirmModal.onCancel();
//                     setConfirmModal({ ...confirmModal, show: false });
//                   }}
//                 >
//                   Cancel
//                 </button>
//               </div>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Notification */}
//       {notification && (
//         <div className="fixed inset-0 flex items-center justify-center z-50">
//           <div
//             className={`${
//               notification.type === "success"
//                 ? "bg-green-100 text-green-700"
//                 : "bg-red-100 text-red-700"
//             } border p-[100px] rounded relative`}
//             role="alert"
//           >
//             <span className="block sm:inline">{notification.message}</span>
//             <button
//               className="absolute top-0 right-0 p-4"
//               onClick={() => setNotification(null)}
//             >
//               ×
//             </button>
//           </div>
//         </div>
//       )}

//       {isMobile && (
//         <div
//           className="fixed top-0 left-0 z-50 flex items-center md:hidden"
//           style={{ marginTop: "21px", marginLeft: "16px" }}
//         >
//           <button
//             onClick={() => setMobileSidebarVisible(true)}
//             className="p-1 rounded-md focus:outline-none hover:bg-gray-700 transition-all duration-200"
//             style={{
//               boxShadow: "none",
//               border: "none",
//               background: "transparent",
//               transform: "none",
//               padding: 0,
//               overflow: "visible",
//             }}
//             aria-label="Open menu"
//           >
//             <Menu className="w-6 h-6 text-gray-300" />
//           </button>
//         </div>
//       )}
//       <div className="flex flex-col md:flex-row min-h-screen bg-gray-100 relative">
//         {!isMobile && renderSidebar()}
//         {isMobile && mobileSidebarVisible && (
//           <>
//             {/* Dark overlay behind the sidebar */}
//             <div
//               className="fixed inset-0 bg-black bg-opacity-50 z-40"
//               onClick={() => setMobileSidebarVisible(false)}
//             ></div>
//             {/* Mobile sidebar */}
//             <div className="fixed inset-y-0 left-0 z-50 w-64 bg-white">
//               {renderSidebar()}
//             </div>
//           </>
//         )}
//         <div className="flex-1 p-8">
//           {mobileLoading && (
//             <div className="fixed inset-0 flex flex-col items-center justify-center bg-white z-50">
//               <img src="../images/loading.gif" />
//             </div>
//           )}
//           {renderContent()}
//         </div>

//         <style>
//           {`
//           .sidebar-nav {
//             overflow-y: visible !important;

//           }
//           @media screen and (max-height: 768px) {
//             .sidebar-nav {
//               flex-wrap: nowrap !important;
//               flex-direction: column !important;
//               overflow-y: visible !important;
//               overflow-x: hidden !important;
//             }
//           }
//         `}
//         </style>
//       </div>
//     </>
//   );
// };

// export default AdminDashboard;
